import { QueryItemContent } from '@discover-frontend/components';
import { Icon, ItemPic } from '@cubeyou-team/hx-react-components';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getItemPic, getItemTypeToShow } from 'src/utils';
import {
  StyledSavedQueryIcon,
  StyledSavedQueryIconContainer,
  StyledSearchResultsItem,
} from './StyledInlineResultSingleItem';

const InlineResultSingleItem = ({
  item,
  tabIndex,
  onItemSelected,
  variant,
  disabled = false,
  ...rest
}) => {
  const ref = useRef();
  const selectedDataSource = useSelector(
    state => state.queryBuilder.dataSource
  );

  const { label, taxonomy, type, pic, source, followers_count, feeds } = item;

  useEffect(() => {
    function handleArrowKeyPressNav(e) {
      const active = document.activeElement;
      if (e.keyCode === 40 && active.nextSibling) {
        e.preventDefault();
        active.nextSibling.focus();
      }
      if (e.keyCode === 38) {
        if (active.previousSibling) {
          return active.previousSibling.focus();
        }
        const inlineSearchInput = document.getElementsByClassName(
          'hx-inline-search-input'
        )[0];
        inlineSearchInput.focus();

        setTimeout(() => {
          inlineSearchInput.select();
        }, 50);
      }

      if (e.keyCode === 13) {
        if (disabled) return;
        onItemSelected(item);
      }
    }

    const node = ref.current;
    node.addEventListener('keydown', handleArrowKeyPressNav);

    return () => {
      node.removeEventListener('keydown', handleArrowKeyPressNav);
    };
  }, [tabIndex, item, onItemSelected, disabled]);

  const itemType = getItemTypeToShow(type);

  const itemIcon = useMemo(() => {
    if (type === 'membership' || type === 'interests') {
      return (
        <StyledSavedQueryIconContainer size="medium">
          <StyledSavedQueryIcon>
            <Icon
              icon={type !== 'membership' ? 'heart' : 'conversation'}
              variant="light"
            />
          </StyledSavedQueryIcon>
        </StyledSavedQueryIconContainer>
      );
    }

    return <ItemPic pic={getItemPic(pic, feeds)} label={label} size="medium" />;
  }, [type, pic, feeds, label]);

  return (
    <React.Fragment>
      <StyledSearchResultsItem
        ref={ref}
        tabIndex={tabIndex}
        isActive={item.isActive}
        key={item.id}
        variant={variant}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          onItemSelected(item);
        }}
        {...rest}
      >
        <QueryItemContent
          itemIcon={itemIcon}
          label={label}
          source={source}
          selectedDataSource={selectedDataSource}
          itemType={itemType}
          followers_count={followers_count}
          taxonomy={taxonomy}
          tooltipPosition="left"
        />
      </StyledSearchResultsItem>
    </React.Fragment>
  );
};

InlineResultSingleItem.propTypes = {
  item: PropTypes.object,
  onItemSelected: PropTypes.func,
  variant: PropTypes.oneOf(['light', 'light_dark']),
  disabled: PropTypes.bool,
};

InlineResultSingleItem.defaultProps = {
  variant: 'light',
  disabled: false,
};

export default InlineResultSingleItem;
